/**
 * SHARED CONTENT PREVIEW — Premium Visual Presentation
 * =====================================================
 * 
 * FOUNDER PRINCIPLE:
 * "Shared content should feel like something Arvexis and the user
 *  are exploring together, not simply files being uploaded."
 * 
 * Every attachment should have a premium visual presentation:
 * - Images with elegant thumbnails
 * - PDFs with recognizable preview cards
 * - Audio with waveform visualization
 * - Links with attractive previews
 * 
 * The conversation remains the center. Attachments enrich, never interrupt.
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Image, Link2, Music, Film, Play, Pause, ExternalLink, Loader2, Maximize2, X, Download } from 'lucide-react';
import { usePinchZoom } from '../hooks/useGestureGuards';

// =============================================================================
// PREMIUM IMAGE PREVIEW — Elegant thumbnail with tap to view full size
// 
// FOUNDER DIRECTION (December 2025):
// "Shared content should behave like living content, not static previews.
//  If the user can see it, they should also be able to tap it, reopen it,
//  inspect it, zoom it, review it again later."
// =============================================================================
const ImagePreview = ({ thumbnail, name, uploading, fullUrl }) => {
  const [loaded, setLoaded] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const zoom = usePinchZoom();

  const closeFullImage = () => {
    setShowFullImage(false);
    zoom.reset();
  };

  // A reloaded conversation has no local copy of the picture, because member uploads are not
  // retained. Rather than render a broken image, the card says plainly what was shared. This is
  // the honest floor until durable previews are a decided architecture.
  if (!thumbnail && !fullUrl) {
    return (
      <div
        className="flex items-center gap-3 p-3 mb-2 rounded-xl bg-white/[0.04] border border-white/[0.06]"
        data-testid="shared-image-placeholder"
      >
        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
          <Image className="w-5 h-5 text-white/40" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-white/80 truncate">{name || 'Photo'}</p>
          <p className="text-[10px] text-white/35">Shared earlier in this conversation</p>
        </div>
      </div>
    );
  }
  
  const handleClick = () => {
    if (!uploading && (fullUrl || thumbnail)) {
      setShowFullImage(true);
    }
  };
  
  return (
    <>
      <div 
        className={`relative overflow-hidden rounded-xl mb-2 ${!uploading ? 'cursor-pointer' : ''}`}
        onClick={handleClick}
      >
        {/* Elegant container with subtle shadow */}
        <div 
          className="relative bg-white/5 rounded-xl overflow-hidden group"
          style={{
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
          }}
        >
          {/* Loading skeleton */}
          {!loaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10 animate-pulse" />
          )}
          
          {/* Thumbnail */}
          <img 
            src={thumbnail} 
            alt={name}
            className={`max-w-full max-h-52 w-auto rounded-xl object-cover transition-all duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${!uploading ? 'group-hover:scale-[1.02]' : ''}`}
            onLoad={() => setLoaded(true)}
          />
          
          {/* Uploading overlay */}
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm rounded-xl">
              <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
            </div>
          )}
          
          {/* Tap to expand hint */}
          {!uploading && loaded && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 className="w-5 h-5 text-white/80" />
              </div>
            </div>
          )}
        </div>
        
        {/* Filename - subtle, not prominent */}
        <p className="text-[10px] text-white/40 mt-1.5 truncate">{name}</p>
      </div>
      
      {/* Full-screen image viewer — pinch and pan live here, and never reach the conversation */}
      {showFullImage && (
        <div 
          className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeFullImage}
          data-testid="shared-image-viewer"
        >
          <button
            onClick={closeFullImage}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close"
            data-testid="shared-image-viewer-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div
            ref={zoom.ref}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={zoom.toggleZoom}
            className="max-w-full max-h-full overflow-hidden"
            data-testid="shared-image-zoom-surface"
          >
            <img
              src={fullUrl || thumbnail}
              alt={name}
              draggable={false}
              className="max-w-full max-h-full object-contain rounded-lg select-none"
              style={zoom.style}
            />
          </div>
        </div>
      )}
    </>
  );
};

// =============================================================================
// PREMIUM PDF PREVIEW — Document card with tap to open
// =============================================================================
const PDFPreview = ({ name, size_bytes, uploading, pageCount, fileUrl }) => {
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  const handleClick = () => {
    if (!uploading && fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative group mb-2"
    >
      {/* Document card */}
      <div 
        className={`flex items-start gap-3 p-3 rounded-xl bg-gradient-to-br from-rose-500/10 to-orange-500/10 border border-rose-500/20 transition-all ${!uploading && fileUrl ? 'cursor-pointer hover:border-rose-400/40 hover:bg-rose-500/15' : ''}`}
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
        }}
        onClick={handleClick}
      >
        {/* PDF Icon with document styling */}
        <div className="relative flex-shrink-0">
          <div 
            className="w-12 h-14 rounded-lg bg-gradient-to-br from-rose-500/30 to-orange-500/30 flex items-center justify-center relative overflow-hidden"
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}
          >
            {/* Document fold corner effect */}
            <div 
              className="absolute top-0 right-0 w-3 h-3 bg-[#1A1A24]"
              style={{
                clipPath: 'polygon(100% 0, 100% 100%, 0 0)'
              }}
            />
            <div 
              className="absolute top-0 right-0 w-3 h-3 bg-gradient-to-br from-rose-400/50 to-orange-400/50"
              style={{
                clipPath: 'polygon(0 0, 100% 100%, 0 100%)'
              }}
            />
            
            <FileText className="w-6 h-6 text-rose-300" />
          </div>
          
          {/* Uploading indicator */}
          {uploading && (
            <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-rose-300 animate-spin" />
            </div>
          )}
        </div>
        
        {/* Document info */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-sm text-white/90 font-medium truncate leading-tight group-hover:text-rose-200 transition-colors">{name}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-rose-300/80 font-medium tracking-wide">PDF</span>
            {size_bytes && (
              <>
                <span className="text-white/20">•</span>
                <span className="text-[10px] text-white/40">{formatSize(size_bytes)}</span>
              </>
            )}
            {pageCount && (
              <>
                <span className="text-white/20">•</span>
                <span className="text-[10px] text-white/40">{pageCount} pages</span>
              </>
            )}
          </div>
          {/* Open hint */}
          {!uploading && fileUrl && (
            <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <ExternalLink className="w-2.5 h-2.5 text-rose-300/60" />
              <span className="text-[10px] text-rose-300/60">Tap to open</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// PREMIUM AUDIO PREVIEW — Waveform visualization with playback
// =============================================================================
const AudioPreview = ({ name, size_bytes, uploading, audioUrl, duration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Generate static waveform bars (visual representation)
  const waveformBars = Array.from({ length: 32 }, (_, i) => {
    // Create a pseudo-random but consistent pattern
    const seed = (i * 7 + 13) % 17;
    const height = 20 + (seed / 17) * 60; // 20-80% height
    return height;
  });
  
  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  useEffect(() => {
    if (audioRef.current) {
      const handleTimeUpdate = () => {
        const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
        setProgress(prog);
      };
      const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
      };
      
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('ended', handleEnded);
      
      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
          audioRef.current.removeEventListener('ended', handleEnded);
        }
      };
    }
  }, [audioUrl]);
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative mb-2"
    >
      {/* Audio URL for playback */}
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}
      
      {/* Audio card */}
      <div 
        className="p-3 rounded-xl bg-gradient-to-br from-purple-500/10 to-violet-500/10 border border-purple-500/20"
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
        }}
      >
        <div className="flex items-center gap-3">
          {/* Play button */}
          <button
            onClick={togglePlayback}
            disabled={!audioUrl || uploading}
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              uploading 
                ? 'bg-purple-500/20 cursor-not-allowed'
                : 'bg-purple-500/30 hover:bg-purple-500/40 active:scale-95'
            }`}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 text-purple-300 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-4 h-4 text-purple-200" />
            ) : (
              <Play className="w-4 h-4 text-purple-200 ml-0.5" />
            )}
          </button>
          
          {/* Waveform visualization */}
          <div className="flex-1 min-w-0">
            <div className="flex items-end gap-[2px] h-8 mb-1.5">
              {waveformBars.map((height, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-full transition-colors duration-150"
                  style={{
                    height: `${height}%`,
                    backgroundColor: (progress / 100) * waveformBars.length > i 
                      ? 'rgba(192, 132, 252, 0.7)' 
                      : 'rgba(192, 132, 252, 0.25)'
                  }}
                />
              ))}
            </div>
            
            {/* File info */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-white/70 truncate max-w-[60%]">{name}</p>
              <div className="flex items-center gap-2 text-[10px] text-white/40">
                {duration && <span>{formatDuration(duration)}</span>}
                {size_bytes && <span>{formatSize(size_bytes)}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// PREMIUM LINK PREVIEW — Attractive card with metadata
// =============================================================================
const LinkPreview = ({ name, url, uploading, title, favicon, description }) => {
  // Extract domain from URL
  const getDomain = (urlStr) => {
    try {
      const domain = new URL(urlStr).hostname;
      return domain.replace('www.', '');
    } catch {
      return urlStr;
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative mb-2"
    >
      {/* Link card */}
      <div 
        className="p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 cursor-pointer hover:border-blue-400/30 transition-colors group"
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
        }}
        onClick={() => url && window.open(url, '_blank')}
      >
        <div className="flex items-start gap-3">
          {/* Link icon or favicon */}
          <div 
            className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"
          >
            {favicon ? (
              <img src={favicon} alt="" className="w-5 h-5 rounded" />
            ) : uploading ? (
              <Loader2 className="w-4 h-4 text-blue-300 animate-spin" />
            ) : (
              <Link2 className="w-5 h-5 text-blue-300" />
            )}
          </div>
          
          {/* Link info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white/90 font-medium line-clamp-1 group-hover:text-blue-200 transition-colors">
              {title || name || url}
            </p>
            {description && (
              <p className="text-xs text-white/50 line-clamp-2 mt-0.5">{description}</p>
            )}
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-[10px] text-blue-300/80">{getDomain(url || name)}</span>
              <ExternalLink className="w-2.5 h-2.5 text-white/30 group-hover:text-blue-300/50 transition-colors" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// PREMIUM TEXT/DOCUMENT PREVIEW — Clean document card with tap to download
// =============================================================================
const TextPreview = ({ name, size_bytes, uploading, excerpt, fileUrl }) => {
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Get file extension for label
  const getExtension = (filename) => {
    const ext = filename.split('.').pop()?.toUpperCase();
    return ext || 'TXT';
  };
  
  const handleClick = () => {
    if (!uploading && fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative mb-2 group"
    >
      {/* Document card */}
      <div 
        className={`flex items-start gap-3 p-3 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/20 transition-all ${!uploading && fileUrl ? 'cursor-pointer hover:border-amber-400/40 hover:bg-amber-500/15' : ''}`}
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
        }}
        onClick={handleClick}
      >
        {/* Document icon */}
        <div className="relative flex-shrink-0">
          <div 
            className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
            ) : (
              <FileText className="w-5 h-5 text-amber-400" />
            )}
          </div>
        </div>
        
        {/* Document info */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-sm text-white/90 font-medium truncate leading-tight group-hover:text-amber-200 transition-colors">{name}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-amber-400/80 font-medium tracking-wide">{getExtension(name)}</span>
            {size_bytes && (
              <>
                <span className="text-white/20">•</span>
                <span className="text-[10px] text-white/40">{formatSize(size_bytes)}</span>
              </>
            )}
          </div>
          {excerpt && (
            <p className="text-[11px] text-white/50 mt-1.5 line-clamp-2 italic">{excerpt}</p>
          )}
          {/* Open hint */}
          {!uploading && fileUrl && (
            <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Download className="w-2.5 h-2.5 text-amber-400/60" />
              <span className="text-[10px] text-amber-400/60">Tap to view</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// VIDEO PREVIEW — a shared clip, while and after Arvexis takes it in
//
// A sent video shows its own first frame with a play treatment, and opens a real player on tap.
// The frame and the playable source are local to this session (the member's own file); when
// neither exists — a reloaded conversation, or a clip whose frame could not be drawn — the card
// falls back to name and type rather than showing a picture it does not have.
// =============================================================================
const VideoPreview = ({ name, size_bytes, uploading, poster, playableUrl }) => {
  const [showPlayer, setShowPlayer] = useState(false);
  const zoom = usePinchZoom();
  const closePlayer = () => {
    setShowPlayer(false);
    zoom.reset();
  };
  const formatSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };
  const extension = (name || '').split('.').pop()?.toUpperCase() || 'VIDEO';

  if (poster) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="relative mb-2"
          data-testid="shared-video-preview"
        >
          <div
            className={`relative overflow-hidden rounded-xl bg-black/40 ${playableUrl && !uploading ? 'cursor-pointer' : ''}`}
            style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)' }}
            onClick={() => { if (playableUrl && !uploading) setShowPlayer(true); }}
            data-testid="shared-video-poster"
          >
            <img src={poster} alt={name} className="max-w-full max-h-52 w-auto rounded-xl object-cover" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20">
                {uploading ? (
                  <Loader2 className="w-5 h-5 text-white/90 animate-spin" />
                ) : (
                  <Play className="w-5 h-5 text-white fill-current ml-0.5" />
                )}
              </div>
            </div>
          </div>
          <p className="text-[10px] text-white/40 mt-1.5 truncate">
            {name}{size_bytes ? ` · ${formatSize(size_bytes)}` : ''}
          </p>
        </motion.div>

        {showPlayer && playableUrl && (
          <div
            className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closePlayer}
            data-testid="shared-video-player"
          >
            <button
              onClick={closePlayer}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Close"
              data-testid="shared-video-player-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <div
              ref={zoom.ref}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full overflow-hidden"
              data-testid="shared-video-zoom-surface"
            >
              <video
                src={playableUrl}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-full rounded-lg"
                style={zoom.style}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  return (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
    className="inline-block max-w-[280px]"
    data-testid="shared-video-preview"
  >
    <div
      className="flex items-start gap-3 p-3 rounded-2xl bg-white/[0.04] border border-white/[0.06]"
      style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)' }}
    >
      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        {uploading ? (
          <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
        ) : (
          <Film className="w-5 h-5 text-amber-400" />
        )}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-sm text-white/90 font-medium truncate leading-tight">{name}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-amber-400/80 font-medium tracking-wide">{extension}</span>
          {size_bytes && (
            <>
              <span className="text-white/20">•</span>
              <span className="text-[10px] text-white/40">{formatSize(size_bytes)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  </motion.div>
  );
};

// =============================================================================
// MAIN EXPORT — SharedContentPreview with type routing
// 
// FOUNDER DIRECTION (December 2025):
// "Shared content should behave like living content, not static previews.
//  If the user can see it, they should also be able to: tap it, reopen it,
//  inspect it, zoom it, review it again later."
// =============================================================================
const SharedContentPreview = ({ content }) => {
  const { 
    type, 
    name, 
    thumbnail, 
    url, 
    size_bytes, 
    uploading,
    title,
    favicon,
    description,
    excerpt,
    pageCount,
    duration,
    audioUrl,
    fileUrl,  // URL to the actual file for reopening
    fullUrl,  // Full resolution URL for images
    poster,      // Locally drawn first frame of a sent video (session-scoped)
    playableUrl  // Local source for playing a sent video back (session-scoped)
  } = content;
  
  // Route to appropriate premium preview
  switch (type) {
    case 'image':
      return <ImagePreview thumbnail={thumbnail} name={name} uploading={uploading} fullUrl={fullUrl || thumbnail} />;
    
    case 'pdf':
      return <PDFPreview name={name} size_bytes={size_bytes} uploading={uploading} pageCount={pageCount} fileUrl={fileUrl || url} />;
    
    case 'audio':
      return <AudioPreview name={name} size_bytes={size_bytes} uploading={uploading} audioUrl={audioUrl} duration={duration} />;
    
    case 'video':
      return <VideoPreview name={name} size_bytes={size_bytes} uploading={uploading} poster={poster || thumbnail} playableUrl={playableUrl} />;
    
    case 'link':
      return <LinkPreview name={name} url={url} uploading={uploading} title={title} favicon={favicon} description={description} />;
    
    case 'text':
    default:
      return <TextPreview name={name} size_bytes={size_bytes} uploading={uploading} excerpt={excerpt} fileUrl={fileUrl || url} />;
  }
};

export default SharedContentPreview;
